// A Boo's setup, step by step, from the conditions the backend reports on
// GET /api/containers/:id/deployment-status (boo-backend lib/reconcile).
// Labels are customer copy: no "AI", "agent" or "container".

export const SETUP_CONDITIONS = ["Running", "Routed", "DNSPublished", "CertificateReady", "Reachable", "Configured"] as const;
export type SetupConditionName = (typeof SETUP_CONDITIONS)[number];

export type SetupCondition = { status: "true" | "false" | "unknown"; reason?: string; message?: string; since: string };
export type SetupConditions = Partial<Record<SetupConditionName, SetupCondition>>;

export const SETUP_STEP_LABELS: Record<SetupConditionName, string> = {
  Running: "Starting your Boo",
  Routed: "Connecting it to the network",
  DNSPublished: "Publishing its address",
  CertificateReady: "Securing its address",
  Reachable: "Checking it answers",
  Configured: "Loading its settings",
};

export type SetupStep = {
  name: SetupConditionName;
  label: string;
  state: "done" | "current" | "waiting";
  // Why it isn't done yet, for staff; customers see only the state.
  detail?: string;
};

export function setupSteps(conditions: SetupConditions | undefined): SetupStep[] | null {
  if (!conditions || Object.keys(conditions).length === 0) return null;
  let current = false;
  return SETUP_CONDITIONS.map((name) => {
    const c = conditions[name];
    const done = c?.status === "true";
    const state = done ? "done" : current ? "waiting" : "current";
    if (!done) current = true;
    const detail = !done && c ? [c.reason, c.message].filter(Boolean).join(": ") || undefined : undefined;
    return { name, label: SETUP_STEP_LABELS[name], state, ...(detail ? { detail } : {}) };
  });
}

// What the checklist renders: reasons only when staff details are on.
export function checklistItems(conditions: SetupConditions | undefined, showDetails: boolean): SetupStep[] | null {
  const steps = setupSteps(conditions);
  if (!steps) return null;
  return showDetails ? steps : steps.map((step) => ({ name: step.name, label: step.label, state: step.state }));
}

// One line for screen readers, re-read when it changes.
export function checklistSummary(steps: SetupStep[]): string {
  const done = steps.filter((s) => s.state === "done").length;
  const current = steps.find((s) => s.state === "current");
  return `${done} of ${steps.length} setup steps done${current ? `, ${current.label} in progress` : ""}.`;
}
